import type { Panel } from "@skyware-group/iris-api-model";

import { BaseClient } from "../../clients/base/BaseClient";
import type { Paged, PaginationParams } from "../_shared/pagination.types";
import { PANELS_ENDPOINTS } from "./panels.consts";
import type { PanelCreateRequest, PanelUpdateRequest } from "./panels.types";

/**
 * Panels API Client (admin only)
 *
 * Handles CRUD for examination panels under an instrument. Sub-resources
 * (mappings, tests) live in their own clients (`panelMappings`, `panelTests`).
 */
export class PanelsClient extends BaseClient {
  /** Fetch a page of panels for an instrument. */
  async fetchPanels(
    instrumentId: number,
    params?: PaginationParams,
    signal?: AbortSignal | null,
  ): Promise<Paged<Panel>> {
    return this.get<Paged<Panel>>(PANELS_ENDPOINTS.PANELS(instrumentId), {
      params: params as Record<string, unknown>,
      signal,
    });
  }

  async fetchPanelById(instrumentId: number, panelId: number, signal?: AbortSignal | null): Promise<Panel> {
    return this.get<Panel>(PANELS_ENDPOINTS.PANEL_BY_ID(instrumentId, panelId), { signal });
  }

  async createPanel(instrumentId: number, data: PanelCreateRequest, signal?: AbortSignal | null): Promise<Panel> {
    return this.post<Panel>(PANELS_ENDPOINTS.PANELS(instrumentId), { body: data, signal });
  }

  async updatePanel(
    instrumentId: number,
    panelId: number,
    data: PanelUpdateRequest,
    signal?: AbortSignal | null,
  ): Promise<Panel> {
    return this.put<Panel>(PANELS_ENDPOINTS.UPDATE_PANEL(instrumentId, panelId), {
      body: data,
      signal,
    });
  }

  async deletePanel(instrumentId: number, panelId: number, signal?: AbortSignal | null): Promise<void> {
    return this.delete<never>(PANELS_ENDPOINTS.DELETE_PANEL(instrumentId, panelId), { signal });
  }
}
